import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface HomeworkSubmission {
  id: string;
  module_id: string;
  user_id: string;
  content: string | null;
  file_url: string | null;
  submitted_at: string;
  instructor_feedback: string | null;
  instructor_reviewed_at: string | null;
  instructor_reviewed_by: string | null;
  author?: {
    full_name: string;
    avatar_url: string | null;
  };
}

export interface PeerReview {
  id: string;
  submission_id: string;
  reviewer_id: string;
  feedback: string;
  rating: number | null;
  created_at: string;
  updated_at: string;
  reviewer?: {
    full_name: string;
    avatar_url: string | null;
  };
}

export function useModuleSubmissions(moduleId: string) {
  return useQuery({
    queryKey: ["homework-submissions", moduleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("homework_submissions")
        .select("*")
        .eq("module_id", moduleId)
        .order("submitted_at", { ascending: false });

      if (error) throw error;

      const userIds = [...new Set(data.map((s) => s.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      return data.map((submission) => ({
        ...submission,
        author: profiles?.find((p) => p.id === submission.user_id),
      })) as HomeworkSubmission[];
    },
    enabled: !!moduleId,
  });
}

export function useMySubmission(moduleId: string, userId: string) {
  return useQuery({
    queryKey: ["my-homework-submission", moduleId, userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("homework_submissions")
        .select("*")
        .eq("module_id", moduleId)
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;
      return data as HomeworkSubmission | null;
    },
    enabled: !!moduleId && !!userId,
  });
}

export function useSubmitHomework() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      moduleId,
      userId,
      content,
      fileUrl,
    }: {
      moduleId: string;
      userId: string;
      content: string;
      fileUrl?: string;
    }) => {
      const { data, error } = await supabase
        .from("homework_submissions")
        .insert({
          module_id: moduleId,
          user_id: userId,
          content,
          file_url: fileUrl || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["homework-submissions", variables.moduleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["my-homework-submission", variables.moduleId],
      });
    },
  });
}

export function useUpdateHomework() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      submissionId,
      content,
      fileUrl,
    }: {
      submissionId: string;
      content: string;
      fileUrl?: string;
    }) => {
      const { data, error } = await supabase
        .from("homework_submissions")
        .update({
          content,
          file_url: fileUrl || null,
        })
        .eq("id", submissionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homework-submissions"] });
      queryClient.invalidateQueries({ queryKey: ["my-homework-submission"] });
    },
  });
}

export function useAddInstructorFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      submissionId,
      feedback,
      instructorId,
    }: {
      submissionId: string;
      feedback: string;
      instructorId: string;
    }) => {
      const { error } = await supabase
        .from("homework_submissions")
        .update({
          instructor_feedback: feedback,
          instructor_reviewed_at: new Date().toISOString(),
          instructor_reviewed_by: instructorId,
        })
        .eq("id", submissionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homework-submissions"] });
    },
  });
}

export function useSubmissionPeerReviews(submissionId: string) {
  return useQuery({
    queryKey: ["peer-reviews", submissionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("peer_reviews")
        .select("*")
        .eq("submission_id", submissionId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const reviewerIds = [...new Set(data.map((r) => r.reviewer_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", reviewerIds);

      return data.map((review) => ({
        ...review,
        reviewer: profiles?.find((p) => p.id === review.reviewer_id),
      })) as PeerReview[];
    },
    enabled: !!submissionId,
  });
}

export function useCreatePeerReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      submissionId,
      reviewerId,
      feedback,
      rating,
    }: {
      submissionId: string;
      reviewerId: string;
      feedback: string;
      rating?: number;
    }) => {
      const { data, error } = await supabase
        .from("peer_reviews")
        .insert({
          submission_id: submissionId,
          reviewer_id: reviewerId,
          feedback,
          rating: rating || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["peer-reviews", variables.submissionId],
      });
    },
  });
}

export function useDeletePeerReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reviewId: string) => {
      const { error } = await supabase
        .from("peer_reviews")
        .delete()
        .eq("id", reviewId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["peer-reviews"] });
    },
  });
}
